import { isEmpty } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { withoutLegacyModel } from '@/reducers/prompts/prompts.helpers'
import { canApply, isMissingItsRewrite, KIND } from '@/views/Costimizer/Costimizer.helpers'

import {
  RUN_STATUS,
  setApplying,
  setOrgError,
  setOrgLoading,
  setOrgScorecards,
  setOrgs,
  setOrgsError,
  setOrgsLoading,
  setResultsLoading,
  setRun,
  setRunError,
  setRunResults,
} from './costimizer.redux'

/*
 * The costimizer's endpoints, on summary-service (`routers/costimizer.py`).
 *
 * No endpoint here reports spend. Copilot cost lives in `prompt_runs`, where a 30-day fleet-wide aggregate is
 * millions of rows behind a four-table join — not something a page load can do — and prompt engineers already
 * have that data elsewhere. What this page reports instead is measured per-prompt cost REDUCTION, which comes
 * free with a run because both sides of it are billed rows on the same calls.
 *
 *   GET  /costimizer/orgs
 *        [{ organization_id, name }]
 *
 *   GET  /costimizer/orgs/:id/scorecards
 *        [{ scorecard_config_id, config_ids, name, minutes_per_subject,
 *           subjects: [{ key, kind, name, model, on_target }],
 *           run: { status, flow_run_id, started_at } | null }]
 *        `config_ids` is every version of the scorecard, and every route here accepts any of them: the id
 *        moves whenever the scorecard is saved, which applying a migration does.
 *        `minutes_per_subject` is how long one prompt takes to climb the ladder, for the estimate shown
 *        before a run starts. The server owns it because the policy that decides it — sample size,
 *        concurrency — is a flow parameter, changeable without a deploy.
 *
 *   POST /costimizer/scorecards/:id/migrate  { subject_keys? }  -> { status, flow_run_id }
 *        Omit `subject_keys` for the whole scorecard; pass them to test only those prompts. Either way it is
 *        one run on one scorecard, so the status is per scorecard.
 *
 *   GET  /costimizer/scorecards/:id/run                -> { status, flow_run_id, started_at, subject_keys }
 *   GET  /costimizer/scorecards/:id/results            -> see `toResults`
 *
 * ⚠ Applying is NOT an endpoint here. Putting a prompt live means minting a prompt version and then a new
 * scorecard config version, and `scorecard_configs`/`scorecard_criteria` belong to scorecard-service — so
 * `applyMigration` below goes through the same two calls the criterion editor uses rather than asking
 * summary-service to reimplement that versioning.
 */

const toSubject = (data) => ({
  key: data.key,
  // The same subject across VERSIONS. `key` names one — a criterion's id, or the eligibility prompt's config
  // id — and every scorecard save mints new ones, so this is what a result is matched to.
  lineageKey: data.lineage_key ?? null,
  kind: data.kind,
  name: data.name,
  model: data.model,
  // Already on the target model, so there is nothing left to migrate on it. The server sends every prompt on
  // the scorecard and flags these rather than leaving them off.
  onTarget: data.on_target ?? false,
})

const toOrg = (data) => ({
  organizationId: data.organization_id,
  name: data.name,
})

const toRunSummary = (data) =>
  data
    ? {
        status: data.status,
        flowRunId: data.flow_run_id ?? null,
        startedAt: data.started_at ?? null,
        // Which prompts the run is about. ⚠ NULL WHEN THE FIELD IS ABSENT, never `[]`: an empty list means
        // ALL of them, so defaulting would make a server that does not send it look like a whole-scorecard
        // run and mark every row as being tested.
        subjectKeys: data.subject_keys ?? null,
      }
    : { status: null, flowRunId: null, startedAt: null, subjectKeys: null }

const toScorecard = (data) => ({
  scorecardConfigId: data.scorecard_config_id,
  name: data.name,
  // ⚠ EVERY VERSION OF THIS SCORECARD, because the id moves: `scorecard_configs` is insert-only, so any
  // save mints a new one and a page whose URL carries the old id has to know it is the same scorecard.
  configIds: data.config_ids ?? [data.scorecard_config_id],
  // How long ONE prompt takes, as a bound — the estimate is a range because the quantity is. Null ends fall
  // back to the helper's own numbers.
  perPromptMinutes: {
    min: data.minutes_per_subject_min ?? null,
    max: data.minutes_per_subject_max ?? null,
  },
  subjects: (data.subjects ?? []).map(toSubject),
  run: toRunSummary(data.run),
})

/**
 * One criterion's measured result.
 *
 * `marginPct` is how far the best attempt landed from the prompt's own current score, in percentage points —
 * negative is worse. It is what a threshold is read against.
 *
 * `stale` means the prompt has been edited since this was measured, so the result describes text nobody is
 * running.
 */
const toCriterionResult = (data) => ({
  key: data.key,
  // Which subject this result is about, across versions — matched against a subject's own `lineageKey`.
  lineageKey: data.lineage_key ?? null,
  name: data.name,
  kind: data.kind,
  verdict: data.verdict,
  atRung: data.at_rung ?? null,
  // ⚠ Which rung's prompt applying publishes, independent of any threshold. `atRung` is not that: a result
  // read below the threshold has no winning rung, while its score is the best any rung measured — so
  // publishing on `atRung` alone would put the live wording on the cheap model under the rewrite's number.
  applyRung: data.apply_rung ?? null,
  selfAgreement: data.self_agreement ?? null,
  bestAccuracy: data.best_accuracy ?? null,
  marginPct: data.margin_pct ?? null,
  incumbentModel: data.incumbent_model ?? null,
  newModel: data.new_model ?? null,
  newModelId: data.new_model_id ?? null,
  newPrompt: data.new_prompt ?? null,
  // The criterion's LINEAGE. Applying matches on this rather than the id, because a scorecard save
  // re-versions the criterion and moves its id.
  criterionScid: data.criterion_scid ?? null,
  refusalReason: data.refusal_reason ?? null,
  stale: data.stale ?? false,
  // ⚠ What is live IS this result's prompt — it has been applied. Read BEFORE `stale`, which is true of an
  // applied prompt too, or our own apply reads as somebody having edited the prompt.
  migrated: data.migrated ?? false,
  // Billed cost per scored call on each side, from the run's own rows. Sent as the two figures rather than a
  // ready-made percentage so the inputs stay visible.
  incumbentCostPerCall: data.incumbent_cost_per_call ?? null,
  newCostPerCall: data.new_cost_per_call ?? null,
  // One entry per RUN, not per arm — how the draws disagree is what a reader is after.
  //
  // `labelsWithheld` means an org whose call content is encrypted. The `*Matched` lists still arrive, since
  // they come from the scorer rather than from comparing the labels, so which draws disagreed survives even
  // where the answers do not.
  labelsWithheld: data.labels_withheld ?? false,
  calls: (data.calls ?? []).map((call) => ({
    callId: call.call_id,
    // The server also sends `reference_label`, which is not read: under this comparison it IS the production
    // answer above, so mapping it would put one number on the row twice under two names.
    productionLabel: call.production_label ?? null,
    incumbentLabels: call.incumbent_labels ?? [],
    samePromptLabels: call.same_prompt_labels ?? [],
    newPromptLabels: call.new_prompt_labels ?? [],
    incumbentMatched: call.incumbent_matched ?? [],
    samePromptMatched: call.same_prompt_matched ?? [],
    newPromptMatched: call.new_prompt_matched ?? [],
  })),
})

/**
 * Three counts, not two. A refused prompt measured nothing at all; an incomplete one measured part of a
 * ladder and then hit an error, so it has no answer either — but it DID cost money and it is not a finding
 * about the prompt. Collapsing them would make a run that half broke look like a run that found nothing.
 */
const toResults = (data) => ({
  policy: data.policy ?? null,
  nMeasured: data.n_measured ?? 0,
  nRefused: data.n_refused ?? 0,
  nIncomplete: data.n_incomplete ?? 0,
  criteria: (data.criteria ?? []).map(toCriterionResult),
})

export const fetchOrgs = () => async (dispatch) => {
  dispatch(setOrgsLoading(true))

  try {
    const orgs = await fetchingAPI(`${apiService.summary}/costimizer/orgs`, 'GET', dispatch)
    dispatch(setOrgs(orgs.map(toOrg)))
  } catch (error) {
    dispatch(setOrgsError({ kind: 'fetchFailed', detail: error?.message }))
  }
}

export const fetchOrgScorecards = (organizationId) => async (dispatch) => {
  dispatch(setOrgLoading(organizationId, true))

  try {
    const scorecards = await fetchingAPI(
      `${apiService.summary}/costimizer/orgs/${organizationId}/scorecards`,
      'GET',
      dispatch
    )
    dispatch(setOrgScorecards(organizationId, scorecards.map(toScorecard)))
  } catch (error) {
    dispatch(setOrgError(organizationId, { kind: 'fetchFailed', detail: error?.message }))
  }
}

/**
 * Test a migration. Does not wait: a scorecard is tens of minutes, so the person leaves and the status is read
 * from the server when they come back.
 *
 * @param {string[]} subjectKeys the prompts to test; empty tests the whole scorecard
 */
export const startMigration =
  (scorecardConfigId, subjectKeys = []) =>
  async (dispatch) => {
    // The keys go in with the optimistic status: the caller knows which prompts it asked for, so the row
    // being tested can say so before the POST comes back rather than after the first poll.
    dispatch(setRun(scorecardConfigId, { status: RUN_STATUS.STARTING, subjectKeys }))

    try {
      const run = await fetchingAPI(
        `${apiService.summary}/costimizer/scorecards/${scorecardConfigId}/migrate`,
        'POST',
        dispatch,
        isEmpty(subjectKeys) ? undefined : JSON.stringify({ subject_keys: subjectKeys })
      )
      dispatch(setRun(scorecardConfigId, toRunSummary(run)))
      toast.success(
        isEmpty(subjectKeys)
          ? 'Migration test started. You can leave this page.'
          : 'Migration test started for that prompt. You can leave this page.'
      )
    } catch (error) {
      dispatch(setRun(scorecardConfigId, { status: null }))
      dispatch(setRunError(scorecardConfigId, { kind: 'startFailed', detail: error?.message }))
      toast.error('Could not start the migration test', { cause: error })
    }
  }

export const fetchRun = (scorecardConfigId) => async (dispatch) => {
  try {
    const run = await fetchingAPI(
      `${apiService.summary}/costimizer/scorecards/${scorecardConfigId}/run`,
      'GET',
      dispatch
    )
    dispatch(setRun(scorecardConfigId, toRunSummary(run)))
  } catch (error) {
    dispatch(setRunError(scorecardConfigId, { kind: 'statusFailed', detail: error?.message }))
  }
}

/**
 * Load what a run found.
 *
 * @param {string} flowRunId the run these results describe, recorded so a later run's results are fetched
 *   rather than the ones already in hand being taken for them
 */
export const fetchResults = (scorecardConfigId, flowRunId) => async (dispatch) => {
  dispatch(setResultsLoading(scorecardConfigId, true))

  try {
    const results = await fetchingAPI(
      `${apiService.summary}/costimizer/scorecards/${scorecardConfigId}/results`,
      'GET',
      dispatch
    )
    dispatch(setRunResults(scorecardConfigId, toResults(results), flowRunId))
  } catch (error) {
    dispatch(setRunError(scorecardConfigId, { kind: 'resultsFailed', detail: error?.message }))
  }
}

/**
 * Which criteria a config's sections hold, flattened. Sections and measures are presentation; a migration
 * cares about the criteria under them.
 */
const configCriteria = (config) =>
  config.sections.flatMap((section) => section.measures).flatMap((measure) => measure.criteria)

/**
 * One prompt version carrying a result's model and, where the optimizer rewrote it, its text.
 *
 * Built by spreading the LIVE prompt so nothing else about it moves — a migration changes the model and
 * maybe the wording, and must not quietly reset a transcript window or a system prompt on the way past.
 *
 * ⚠ Keeping the live wording is right for a straight model SWAP, which won with the prompt already in
 * production. It is wrong for a rewrite, so a missing rewrite raises: `criteriaPassingAt` refuses those,
 * and the alternative is publishing the old wording under a result saying a rewrite was needed.
 */
const migratedPrompt = (livePrompt, criterion, organizationId) => {
  if (isMissingItsRewrite(criterion)) {
    throw new Error(`${criterion.key}: the winning rewrite's text is missing`)
  }

  return withoutLegacyModel({
    ...livePrompt,
    organization_id: organizationId,
    llm_model_id: criterion.newModelId,
    user_prompt: criterion.newPrompt ?? livePrompt.user_prompt,
  })
}

/**
 * Put the named results' prompts live — a threshold's worth of them, or a single one.
 *
 * ⚠ The one action here that changes what customers are scored by. It takes the same path as the criterion
 * editor — mint prompt versions on summary-service, then PUT the whole config to scorecard-service, which
 * owns the insert-only config and criterion tables.
 *
 * Two things the order buys: every prompt is minted before the config is written, so a failure part-way
 * leaves prompt rows nothing points at rather than a half-migrated config; and one PUT covers the batch, so
 * the scorecard gains a single new version however many prompts it carries.
 *
 * ⚠ WHICH results to publish is the caller's — a threshold, or one row somebody chose. Whether a result CAN
 * be published is not: `canApply` is checked here, at the write, so no caller can publish a stale result or
 * a rewrite whose text never arrived.
 *
 * @param {object[]} criteria the results to publish, as `toCriterionResult` shapes them
 */
export const applyMigration =
  (scorecardConfigId, { criteria }) =>
  async (dispatch) => {
    dispatch(setApplying(scorecardConfigId, true))

    const applying = criteria.filter(canApply)

    if (isEmpty(applying)) {
      dispatch(setApplying(scorecardConfigId, false))
      toast.error('Nothing here can be applied')

      return
    }

    try {
      const config = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfigId}`,
        'GET',
        dispatch
      )

      // Matched on the criterion's LINEAGE, not its id: a scorecard save re-versions the criterion, so the
      // id the run recorded may no longer be the one in this config.
      const byScid = new Map(
        applying
          .filter((criterion) => criterion.kind === KIND.CRITERION && criterion.criterionScid)
          .map((criterion) => [criterion.criterionScid, criterion])
      )
      // The gate belongs to the CONFIG rather than to a criterion, so it has no `scid` of its own and is
      // repointed through `eligibility_prompt_id` below. One per scorecard, and the results carry one row
      // per subject lineage, so there is never more than one to find.
      const eligibility = applying.find((criterion) => criterion.kind === KIND.ELIGIBILITY)

      const targets = configCriteria(config)
        .filter((onScorecard) => byScid.has(onScorecard.scid))
        .map((onScorecard) => ({ promptId: onScorecard.prompt_id, scid: onScorecard.scid }))

      if (eligibility && config.eligibility_prompt_id) {
        targets.push({ promptId: config.eligibility_prompt_id, scid: KIND.ELIGIBILITY })
      }

      if (isEmpty(targets)) {
        throw new Error('none of those prompts are on this scorecard any more')
      }

      // The live prompts, then a new version of each. Both fanned out, because a scorecard of 145 criteria
      // would otherwise be 145 sequential round trips.
      const livePrompts = await Promise.all(
        targets.map((target) =>
          fetchingAPI(`${apiService.summary}/prompts/${target.promptId}`, 'GET', dispatch)
        )
      )

      const minted = await Promise.all(
        livePrompts.map((livePrompt, index) => {
          const target = targets[index]
          const criterion = target.scid === KIND.ELIGIBILITY ? eligibility : byScid.get(target.scid)

          return fetchingAPI(
            `${apiService.summary}/prompts`,
            'POST',
            dispatch,
            JSON.stringify(migratedPrompt(livePrompt, criterion, config.organization_id))
          ).then((created) => ({ scid: target.scid, id: created.id }))
        })
      )

      const newIdByScid = new Map(minted.map((one) => [one.scid, one.id]))

      const updated = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfigId}`,
        'PUT',
        dispatch,
        JSON.stringify({
          ...config,
          eligibility_prompt_id: newIdByScid.get(KIND.ELIGIBILITY) ?? config.eligibility_prompt_id,
          sections: config.sections.map((section) => ({
            ...section,
            measures: section.measures.map((measure) => ({
              ...measure,
              criteria: measure.criteria.map((onScorecard) => ({
                ...onScorecard,
                prompt_id: newIdByScid.get(onScorecard.scid) ?? onScorecard.prompt_id,
              })),
            })),
          })),
        })
      )

      dispatch(setApplying(scorecardConfigId, false))
      toast.success(`Applied ${minted.length} prompt(s) — now scorecard version ${updated.id}`)
      dispatch(fetchOrgScorecards(config.organization_id))
    } catch (error) {
      dispatch(setApplying(scorecardConfigId, false))
      dispatch(setRunError(scorecardConfigId, { kind: 'applyFailed', detail: error?.message }))
      toast.error(
        error?.status === 423
          ? 'Someone saved this scorecard while you were looking at it. Reload and re-check the results.'
          : 'Could not apply the migration'
      )
    }
  }
